import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { JapaneseVehiclePlatePopoverInput } from '@/components/efs/JapaneseVehiclePlatePopoverInput';
import { Textarea } from '@/components/ui/textarea';
import { LocalizedApiError } from '@/features/audit-issues';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';

import {
  inboundLotWorksheetUpdateSchema,
  type InboundLotWithPackagesDetailDto,
  type InboundLotWorksheetUpdateInput,
} from './types';
import { useUpdateInboundLotWorksheet } from './useInbound';
import { WeightTicketEntryCard } from './WeightTicketEntryCard';

const NS = 'operations';

/** ISO-8601 → value for a `datetime-local` input (`YYYY-MM-DDTHH:mm`, local). */
const isoToLocalInput = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** `datetime-local` value → ISO-8601 string (or undefined when empty). */
const localInputToIso = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  const d = new Date(trimmed);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

interface WorksheetEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lot: InboundLotWithPackagesDetailDto | undefined;
}

/**
 * Edit dialog for the lot-level worksheet (車番, 計量正味, 棧板數,
 * 太空袋數, 段ボール數). Always requires `change_reason` because every
 * edit writes one row to `inbound_lot_revisions` server-side.
 */
export const WorksheetEditDialog = ({ open, onOpenChange, lot }: WorksheetEditDialogProps) => {
  const { t } = useTranslation([NS, 'issues']);
  const currentUser = useCurrentUser();
  const update = useUpdateInboundLotWorksheet();
  const [vehicleEditorOpen, setVehicleEditorOpen] = useState(false);

  const defaults = (): InboundLotWorksheetUpdateInput => ({
    vehicle: lot?.vehicle ?? '',
    measured_net_kg: lot?.measured_net_kg ?? '',
    declared_pallet_count: lot?.declared_pallet_count ?? undefined,
    declared_fureikon_count: lot?.declared_fureikon_count ?? undefined,
    declared_cardboard_count: lot?.declared_cardboard_count ?? undefined,
    arrival_at: isoToLocalInput(lot?.arrival_at),
    departure_at: isoToLocalInput(lot?.departure_at),
    change_reason: '',
  });

  const form = useForm<InboundLotWorksheetUpdateInput>({
    resolver: zodResolver(inboundLotWorksheetUpdateSchema),
    defaultValues: defaults(),
  });

  useEffect(() => {
    if (open) {
      form.reset(defaults());
      setVehicleEditorOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lot?.inbound_lot_id]);

  const onSubmit = form.handleSubmit(async (values) => {
    if (!lot) return;
    const body: InboundLotWorksheetUpdateInput = {
      vehicle: values.vehicle?.trim() ? values.vehicle : undefined,
      measured_net_kg: values.measured_net_kg?.trim() ? values.measured_net_kg : undefined,
      declared_pallet_count:
        values.declared_pallet_count !== undefined &&
        !Number.isNaN(Number(values.declared_pallet_count))
          ? Number(values.declared_pallet_count)
          : undefined,
      declared_fureikon_count:
        values.declared_fureikon_count !== undefined &&
        !Number.isNaN(Number(values.declared_fureikon_count))
          ? Number(values.declared_fureikon_count)
          : undefined,
      declared_cardboard_count:
        values.declared_cardboard_count !== undefined &&
        !Number.isNaN(Number(values.declared_cardboard_count))
          ? Number(values.declared_cardboard_count)
          : undefined,
      // Send '' (not undefined) when cleared so the backend's empty-string
      // clear path runs; an unchanged/empty field is a no-op there (before==after).
      arrival_at: localInputToIso(values.arrival_at) ?? '',
      departure_at: localInputToIso(values.departure_at) ?? '',
      change_reason: values.change_reason.trim(),
    };
    await update.mutateAsync({ lotId: lot.inbound_lot_id, body });
    onOpenChange(false);
  });

  const canViewTechnicalErrors = hasPermission(currentUser, 'system.admin');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] max-w-xl overflow-y-auto"
        onEscapeKeyDown={(event) => {
          if (vehicleEditorOpen) {
            event.preventDefault();
            setVehicleEditorOpen(false);
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {t('efs.operations.inbound.worksheet.edit_title', {
              defaultValue: '進貨工作單 編集',
            })}
          </DialogTitle>
          <DialogDescription>
            {t('efs.operations.inbound.worksheet.edit_subtitle', {
              defaultValue: '変更した項目だけ入力してください。修改原因は必須です（監査ログ）。',
            })}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-3" noValidate>
            <WeightTicketEntryCard
              title={t('efs.operations.inbound.worksheet.ticket.title')}
              hint={t('efs.operations.inbound.worksheet.ticket.hint')}
              timeLabel={t('efs.operations.inbound.worksheet.ticket.time')}
              vehicleLabel={t('efs.operations.inbound.worksheet.ticket.vehicle')}
              grossLabel={t('efs.operations.inbound.worksheet.ticket.gross')}
              tareLabel={t('efs.operations.inbound.worksheet.ticket.tare')}
              netLabel={t('efs.operations.inbound.worksheet.ticket.net')}
              blankValueLabel={t('efs.operations.inbound.worksheet.ticket.blank_value')}
              timeField={
                <FormField
                  control={form.control}
                  name="arrival_at"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className="sr-only">
                        {t('efs.operations.inbound.worksheet.ticket.time')}
                      </FormLabel>
                      <FormControl>
                        <Input type="datetime-local" {...field} value={field.value ?? ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              }
              vehicleField={
                <FormField
                  control={form.control}
                  name="vehicle"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className="sr-only">
                        {t('efs.operations.inbound.worksheet.ticket.vehicle')}
                      </FormLabel>
                      <FormControl>
                        <JapaneseVehiclePlatePopoverInput
                          id="inbound-worksheet-vehicle"
                          ref={field.ref}
                          value={field.value ?? ''}
                          open={vehicleEditorOpen}
                          onOpenChange={setVehicleEditorOpen}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              }
              netField={
                <FormField
                  control={form.control}
                  name="measured_net_kg"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className="sr-only">
                        {t('efs.operations.inbound.worksheet.ticket.net')}
                      </FormLabel>
                      <div className="relative">
                        <FormControl>
                          <Input
                            type="number"
                            inputMode="decimal"
                            step="0.1"
                            min={0}
                            placeholder="8520"
                            className="pr-10 text-base font-semibold tabular-nums"
                            {...field}
                            value={field.value ?? ''}
                          />
                        </FormControl>
                        <span
                          className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-efs-text-muted"
                          aria-hidden="true"
                        >
                          kg
                        </span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              }
            />

            <div className="rounded-md border border-efs-border bg-efs-bg-panel p-3 sm:p-4">
              <p className="text-sm font-semibold">
                {t('efs.operations.inbound.worksheet.ticket.secondary_title')}
              </p>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="departure_at"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('efs.operations.inbound.worksheet.departure_at')}</FormLabel>
                      <FormControl>
                        <Input type="datetime-local" {...field} value={field.value ?? ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <FormField
                  control={form.control}
                  name="declared_pallet_count"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('efs.operations.inbound.worksheet.pallet_count')}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          inputMode="numeric"
                          step={1}
                          min={0}
                          {...field}
                          value={field.value ?? ''}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === '' ? undefined : Number(e.target.value),
                            )
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="declared_fureikon_count"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('efs.operations.inbound.worksheet.fureikon_count')}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          inputMode="numeric"
                          step={1}
                          min={0}
                          {...field}
                          value={field.value ?? ''}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === '' ? undefined : Number(e.target.value),
                            )
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="declared_cardboard_count"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('efs.operations.inbound.worksheet.cardboard_count')}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          inputMode="numeric"
                          step={1}
                          min={0}
                          {...field}
                          value={field.value ?? ''}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === '' ? undefined : Number(e.target.value),
                            )
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <FormField
              control={form.control}
              name="change_reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.operations.inbound.package_edit.change_reason', {
                      defaultValue: '修改原因',
                    })}
                    <span className="ml-1 text-red-900">*</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      rows={2}
                      placeholder={t(
                        'efs.operations.inbound.package_edit.change_reason_placeholder',
                        {
                          defaultValue: '例: 重量を測り直したため修正',
                        },
                      )}
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <p className="text-xs text-efs-text-muted">
                    {t('efs.operations.inbound.worksheet.edit_reason_hint', {
                      defaultValue: '工作單の編集はすべて監査ログに記録されます。',
                    })}
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            {update.error ? (
              <LocalizedApiError
                error={update.error}
                showTechnicalDetails={canViewTechnicalErrors}
                className="text-xs"
              />
            ) : null}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={update.isPending}
              >
                {t('efs.common.actions.cancel', { ns: 'common' })}
              </Button>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                {t('efs.common.actions.save', { ns: 'common', defaultValue: '保存' })}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
